import { useEffect, useState } from "react";
import getDetailsModuleAssetUrl from "mocks/worker/getDetailsModuleAssetUrl.ts";
import { getFileNameBasedOnCurrentDateTime } from "../utils/file";
import { models } from "../types/api/viewModels.ts";
import { useLazyGenerateAcknowledgementsPdfQuery } from "features/api/pdfApi.ts";
import { APIErrorResponse, isApiErrorResponse } from "features/api/utils/apiUtils.ts";
import { AcknowledgmentEmailFormatType } from "../types/declarations/enums.ts";

const useOrderDetailsPDFGenerator = (
	orderDetail: models["OrderDetailViewModel"],
	groupedOrders: models["OrderDetailViewModel"][],
	billToInfo?: models["BillToViewModel"],
	configurationsData?: models["ConfigurationViewModel"]
) => {
	const [pdfData, setPdfData] = useState<PDFData>({ blob: null, loading: false, error: null });
	const [trigger, { data: pdfApiData, error: pdfApiError }] = useLazyGenerateAcknowledgementsPdfQuery();

	const generateOrderDetailsPDF = (format?: AcknowledgmentEmailFormatType) => {
		if (orderDetail.isAcknowledgementEligible && configurationsData?.featureFlags?.acknowledgementsEnabled) {
			trigger({
				orderId: orderDetail.orderId,
				layoutFormat: format
			});
			setPdfData((prev) => ({ ...prev, loading: true }));
		} else {
			const worker = getDetailsModuleAssetUrl();

			worker.postMessage({ orderDetail, groupedOrders, billToInfo, configurationsData });

			worker.onmessage = (e) => {
				if (e.data.blob) {
					setPdfData({ blob: e.data.blob, loading: false, error: null });
				} else if (e.data.error) {
					setPdfData((prevData) => ({ ...prevData, loading: false, error: e.data.error }));
				}
			};

			worker.onerror = (error) => {
				setPdfData((prevData) => ({
					...prevData,
					loading: false,
					error: error instanceof Error ? error : new Error("An unknown error occurred")
				}));
			};

			setPdfData((prevData) => ({ ...prevData, loading: true }));
		}
	};

	const jobName = orderDetail.jobName ?? "";
	const poNumber = orderDetail.poNumber ?? "";
	const filePrefix = `${jobName}_PO${poNumber}`;

	useEffect(() => {
		if (pdfApiData && "type" in pdfApiData) {
			setPdfData({ blob: pdfApiData, loading: false, error: null });
		}

		if (pdfApiError) {
			const error = isApiErrorResponse(pdfApiError) ? pdfApiError : new Error("Failed to generate PDF");
			setPdfData({ blob: null, loading: false, error: error });
		}
	}, [pdfApiData, pdfApiError]);

	useEffect(() => {
		if (pdfData.blob) {
			const url = window.URL.createObjectURL(pdfData.blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${getFileNameBasedOnCurrentDateTime(filePrefix)}.pdf`);
			document.body.appendChild(link);
			link.click();
			setPdfData({ blob: null, loading: false, error: null });
		}
	}, [pdfData.blob, jobName, poNumber, filePrefix]);

	return { pdfData, generateOrderDetailsPDF };
};

interface PDFData {
	blob: Blob | null;
	loading: boolean;
	error: Error | APIErrorResponse | null | undefined;
}

export default useOrderDetailsPDFGenerator;
